/**
 * Utility functions for managing maid time slots and booking conflicts
 */

/**
 * Determines the time slot (AM/PM) based on the job's scheduled start time
 * @param {string|Date} scheduledTime - The job's scheduled start time
 * @returns {string} - 'AM' or 'PM'
 */
export const getTimeSlot = (scheduledTime) => {
  if (!scheduledTime) return null;
  
  const date = new Date(scheduledTime);
  if (isNaN(date.getTime())) return null;
  
  const hour = date.getHours();
  
  // Morning slot: 6:00 AM to 12:00 PM (noon)
  // Afternoon slot: 12:00 PM to 8:00 PM
  return hour < 12 ? 'AM' : 'PM';
};

/**
 * Gets the date string (YYYY-MM-DD) from a scheduled time
 * @param {string|Date} scheduledTime - The job's scheduled time
 * @returns {string} - Date in YYYY-MM-DD format
 */
export const getDateString = (scheduledTime) => {
  if (!scheduledTime) return null;
  
  const date = new Date(scheduledTime);
  if (isNaN(date.getTime())) return null;
  
  return date.toISOString().split('T')[0];
};

/**
 * Checks if a maid is available for assignment to a specific date and time slot
 * A maid is AVAILABLE if they are not already assigned to a job in that time slot
 * @param {Object} maid - The maid object
 * @param {string} targetDate - Target date in YYYY-MM-DD format
 * @param {string} targetTimeSlot - Target time slot ('AM' or 'PM')
 * @param {Array} allJobs - Array of all jobs to check against
 * @returns {boolean} - true if available, false if already booked
 */
export const isMaidAvailableForSlot = (maid, targetDate, targetTimeSlot, allJobs) => {
  const conflictInfo = checkMaidBookingConflict(maid, targetDate, targetTimeSlot, allJobs);
  return !conflictInfo.hasConflict;
};

/**
 * Checks if a maid has conflicting bookings for the same date and time slot
 * @param {Object} maid - The maid object
 * @param {string} targetDate - Target date in YYYY-MM-DD format
 * @param {string} targetTimeSlot - Target time slot ('AM' or 'PM')
 * @param {Array} allJobs - Array of all jobs to check against
 * @returns {Object} - { hasConflict: boolean, conflictingJobs: Array }
 */
export const checkMaidBookingConflict = (maid, targetDate, targetTimeSlot, allJobs) => {
  if (!maid || !targetDate || !targetTimeSlot || !allJobs) {
    return { hasConflict: false, conflictingJobs: [] };
  }
  
  const maidId = maid.id || maid.user?.id || maid.userId;
  if (!maidId) {
    return { hasConflict: false, conflictingJobs: [] };
  }
  
  const conflictingJobs = allJobs.filter(job => {
    // Skip jobs that don't have assignments
    if (!job.assignments || job.assignments.length === 0) return false;
    
    // Check if this maid is assigned to this job
    const isAssignedToJob = job.assignments.some(assignment => {
      const assignedMaidId = assignment.maid?.id || assignment.maid?.userId;
      return assignedMaidId === maidId;
    });
    
    if (!isAssignedToJob) return false;
    
    // Check if it's the same date
    const jobDate = getDateString(job.scheduledStartTime || job.utcScheduledDate || job.scheduledDate);
    if (jobDate !== targetDate) return false;
    
    // Check if it's the same time slot
    const jobTimeSlot = getTimeSlot(job.scheduledStartTime || job.utcScheduledDate || job.scheduledDate);
    return jobTimeSlot === targetTimeSlot;
  });
  
  return {
    hasConflict: conflictingJobs.length > 0,
    conflictingJobs: conflictingJobs
  };
};

/**
 * Formats time slot for display
 * @param {string} timeSlot - 'AM' or 'PM'
 * @returns {string} - Formatted time slot
 */
export const formatTimeSlot = (timeSlot) => {
  if (timeSlot === 'AM') return 'Morning (6AM-12PM)';
  if (timeSlot === 'PM') return 'Afternoon (12PM-8PM)';
  return timeSlot;
};

/**
 * Gets a readable conflict message
 * @param {Object} conflictInfo - Result from checkMaidBookingConflict
 * @param {string} maidName - Name of the maid
 * @param {string} timeSlot - Target time slot
 * @returns {string} - Conflict message
 */
export const getConflictMessage = (conflictInfo, maidName, timeSlot) => {
  if (!conflictInfo.hasConflict) return '';
  
  const jobCount = conflictInfo.conflictingJobs.length;
  const slotName = formatTimeSlot(timeSlot);
  
  if (jobCount === 1) {
    const job = conflictInfo.conflictingJobs[0];
    const customerName = job.customer?.name || job.customer?.customerName || 'Unknown Customer';
    return `${maidName} is already booked in the ${slotName.toLowerCase()} slot for ${customerName}`;
  } else {
    return `${maidName} is already booked in the ${slotName.toLowerCase()} slot (${jobCount} jobs)`;
  }
};